import { LitElement, css, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/alert/alert.js';
import '@shoelace-style/shoelace/dist/components/dialog/dialog.js';
import '@shoelace-style/shoelace/dist/components/input/input.js';
import '@shoelace-style/shoelace/dist/components/badge/badge.js';
import '@shoelace-style/shoelace/dist/components/select/select.js';
import '@shoelace-style/shoelace/dist/components/option/option.js';
import { WithUserProfileConsumer } from '../mixins/with-user-profile-consumer';
import { WithActiveShopConsumer } from '../mixins/with-active-shop-consumer';
import { WithRealtimeConsumer } from '../mixins/with-realtime-consumer';
import { ApiFactory } from '../api/easypos-api';
import { UserProfile } from '../datatypes/user-profile';
import { ILoginAccessToken } from '../api/model/login-access-token';
import {
  ALL_TIERS,
  TIER_LABELS,
  SubscriptionTier,
  SubscriptionStatus,
} from '../datatypes/device';
import { write } from '../language/lang';
import { resolveRouterPath, router } from '../router';

const Base = WithRealtimeConsumer(
  WithActiveShopConsumer(WithUserProfileConsumer(LitElement))
);

// Dev-tools card. Only renders when userProfile.isDevAccount is true. The
// server enforces the same gate (the endpoints 404 for non-dev accounts), so
// hiding the card is purely about discoverability.
@customElement('dev-tools-card')
export class DevToolsCard extends Base {
  @state() private _busy:
    'idle' | 'resetting' | 'deleting' | 'pinging' | 'tier' = 'idle';
  @state() private _message: {
    kind: 'success' | 'danger';
    text: string;
  } | null = null;
  @state() private _confirmShop: string | null = null;
  @state() private _confirmText = '';
  @state() private _resettingShop: string | null = null;
  @state() private _pingLog: { receivedAt: Date; payload: unknown }[] = [];

  private _unsubscribePing?: () => void;

  connectedCallback(): void {
    super.connectedCallback();
    this._maybeSubscribePing();
  }

  disconnectedCallback(): void {
    this._unsubscribePing?.();
    this._unsubscribePing = undefined;
    super.disconnectedCallback();
  }

  updated(changed: Map<string, unknown>) {
    super.updated?.(changed);
    if (changed.has('realtime')) {
      this._unsubscribePing?.();
      this._unsubscribePing = undefined;
      this._maybeSubscribePing();
    }
  }

  private _maybeSubscribePing() {
    if (!this.realtime) return;
    this._unsubscribePing = this.realtime.on('ping', (payload) => {
      this._pingLog = [
        { receivedAt: new Date(), payload },
        ...this._pingLog.slice(0, 9),
      ];
    });
  }

  static styles = css`
    :host {
      display: block;
    }

    .dev-card {
      border-radius: var(--sl-border-radius-large);
      border: 1px dashed var(--sl-color-warning-400);
      background: rgba(245, 158, 11, 0.04);
      padding: 1rem 1.1rem;
    }

    @media (prefers-color-scheme: dark) {
      .dev-card {
        background: rgba(245, 158, 11, 0.08);
      }
    }

    h2 {
      display: flex;
      align-items: center;
      gap: 0.4rem;
      margin: 0 0 0.25rem;
      font-size: 0.95rem;
      font-weight: 600;
      letter-spacing: 0.02em;
      color: var(--sl-color-warning-700);
    }

    @media (prefers-color-scheme: dark) {
      h2 {
        color: var(--sl-color-warning-400);
      }
    }

    p.lede {
      margin: 0 0 0.85rem;
      color: var(--app-text-muted);
      font-size: 0.85rem;
    }

    .actions {
      display: flex;
      flex-wrap: wrap;
      gap: 0.5rem;
    }

    sl-alert {
      margin-top: 0.75rem;
    }

    .shop-actions-list {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      margin: 0.5rem 0 0;
    }
    .shop-row {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 0.5rem;
      padding: 0.4rem 0.6rem;
      border: 1px solid var(--app-border, var(--sl-color-neutral-200));
      border-radius: var(--sl-border-radius-medium);
      background: var(--app-surface, transparent);
    }
    .shop-row-name {
      flex: 1 1 200px;
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 0.4rem;
      font-size: 0.9rem;
    }
    .shop-row-actions {
      display: flex;
      gap: 0.4rem;
    }

    sl-dialog [slot='label'] {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    sl-dialog p {
      margin: 0 0 0.75rem;
      line-height: 1.5;
    }

    sl-dialog code {
      font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
      background: var(--sl-color-neutral-100);
      padding: 1px 6px;
      border-radius: 4px;
    }

    @media (prefers-color-scheme: dark) {
      sl-dialog code {
        background: rgba(255, 255, 255, 0.08);
      }
    }

    .realtime-section {
      margin-top: 1rem;
      padding-top: 0.85rem;
      border-top: 1px dashed var(--sl-color-warning-400);
    }
    .realtime-status {
      display: flex;
      align-items: center;
      gap: 0.4rem;
      margin: 0 0 0.5rem;
      font-size: 0.85rem;
    }
    .ping-log {
      margin: 0.5rem 0 0;
      font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
      font-size: 0.75rem;
      color: var(--app-text-muted);
      max-height: 140px;
      overflow-y: auto;
      padding: 0.4rem 0.6rem;
      background: var(--sl-color-neutral-100);
      border-radius: 4px;
    }
    @media (prefers-color-scheme: dark) {
      .ping-log {
        background: rgba(255, 255, 255, 0.04);
      }
    }
    .ping-log-entry {
      display: block;
      padding: 1px 0;
    }
    .ping-log-empty {
      opacity: 0.6;
    }
  `;

  private get isDev(): boolean {
    return this.userProfile?.accessToken?.isDevAccount === true;
  }

  // After a populate/delete, rebuild the user-profile from a fresh /api/renew
  // so the new shop list (with or without "sample") shows up in the UI without
  // a manual logout.
  private async _refreshProfileAndRoute(activateShop?: string) {
    if (!this.userProfile) return;
    const api = ApiFactory.authenticated();
    const r = await api.renew();
    if (!r.success || !r.data) {
      this._message = {
        kind: 'danger',
        text: 'Done, but failed to refresh your session token. Try logging out and back in.',
      };
      return;
    }
    const next = new UserProfile({
      ...this.userProfile.profile,
      accessToken: r.data,
    });
    window.dispatchEvent(
      new CustomEvent<UserProfile>('user-profile-set', { detail: next })
    );

    // If we just populated, switch to the new shop so the user lands inside it.
    if (activateShop) {
      const access = next.shopAccess.find(
        (s) => s.shopName.toLowerCase() === activateShop.toLowerCase()
      );
      if (access) {
        window.dispatchEvent(
          new CustomEvent('shop-selected', { detail: access })
        );
        router.navigate(resolveRouterPath(''));
      }
    }
  }

  private async _resetSample(shopName: string) {
    if (this._busy !== 'idle') return;
    this._busy = 'resetting';
    this._resettingShop = shopName;
    this._message = null;
    try {
      const r = await ApiFactory.authenticated().devResetSampleShop(shopName);
      if (!r.success || !r.data) {
        this._message = {
          kind: 'danger',
          text: r.error ?? `Could not reset "${shopName}".`,
        };
        return;
      }
      this._message = {
        kind: 'success',
        text: `Reset "${r.data.shopName}" to seed data. Switching to it now.`,
      };
      await this._refreshProfileAndRoute(r.data.shopName);
    } finally {
      this._busy = 'idle';
      this._resettingShop = null;
    }
  }

  private _askConfirmDelete(shopName: string) {
    this._confirmShop = shopName;
    this._confirmText = '';
  }

  private _cancelConfirm() {
    this._confirmShop = null;
    this._confirmText = '';
  }

  private async _sendPing() {
    if (this._busy !== 'idle') return;
    if (!this.activeShop) {
      this._message = { kind: 'danger', text: 'Pick a shop before pinging.' };
      return;
    }
    this._busy = 'pinging';
    this._message = null;
    try {
      const r = await ApiFactory.authenticated().realtimePing(
        this.activeShop.shopName,
        `dev-tools ${new Date().toLocaleTimeString()}`
      );
      if (!r.success) {
        this._message = { kind: 'danger', text: r.error ?? 'Ping failed.' };
      }
    } finally {
      this._busy = 'idle';
    }
  }

  private async _setTier(tier: SubscriptionTier) {
    if (this._busy !== 'idle') return;
    if (!this.activeShop) {
      this._message = {
        kind: 'danger',
        text: 'Pick a shop before changing tier.',
      };
      return;
    }
    if (!this.userProfile) return;
    this._busy = 'tier';
    this._message = null;
    try {
      const r = await ApiFactory.authenticated().devSetShopTier(
        this.activeShop.shopName,
        tier
      );
      if (!r.success || !r.data) {
        this._message = {
          kind: 'danger',
          text: r.error ?? 'Could not change tier.',
        };
        return;
      }
      this._message = {
        kind: 'success',
        text: `Tier set to ${TIER_LABELS[tier]} on "${r.data.shopName}".`,
      };
      // Reflect the change in the user-profile so the badge updates and the
      // new tier survives a refresh. Prefer the server's renewed token (a
      // full fresh snapshot); when the token renew is capped (token === null,
      // MaxVersionNumber hit by repeated dev flips) fall back to patching THIS
      // shop's display snapshot from the authoritative response so the UI
      // still updates AND persists. The token's embedded subscription is
      // display-only — server-side gating reads a fresh shop blob per request
      // — so a patched snapshot can never desync from enforcement.
      const next = this._profileWithTier(r.data);
      if (next) {
        window.dispatchEvent(
          new CustomEvent<UserProfile>('user-profile-set', { detail: next })
        );
      }
    } finally {
      this._busy = 'idle';
    }
  }

  // Build an updated UserProfile reflecting a tier change for the named shop.
  // Uses the renewed token when present; otherwise clones the current profile
  // and patches the matching shop's subscription snapshot in place (keeping
  // the existing encoded token, which stays valid for auth — only the
  // display-only snapshot changes). Returns null when there's no profile.
  private _profileWithTier(data: {
    shopName: string;
    tier: string;
    status: string;
    allowsInventory: boolean;
    allowsCashManagement: boolean;
    token: ILoginAccessToken | null;
  }): UserProfile | null {
    const profile = this.userProfile?.profile;
    if (!profile) return null;
    if (data.token) {
      return new UserProfile({ ...profile, accessToken: data.token });
    }
    const token = profile.accessToken;
    if (!token) return null;
    const target = data.shopName.toLowerCase();
    const shopAccess = token.shopAccess.map((s) =>
      s.shopName.toLowerCase() === target
        ? {
            ...s,
            subscription: {
              // Trial clock is independent of tier and not echoed by the
              // response — preserve whatever the snapshot already had.
              trialDaysRemaining: s.subscription?.trialDaysRemaining ?? 0,
              ...s.subscription,
              tier: data.tier as SubscriptionTier,
              status: data.status as SubscriptionStatus,
              allowsInventory: data.allowsInventory,
              allowsCashManagement: data.allowsCashManagement,
            },
          }
        : s
    );
    return new UserProfile({
      ...profile,
      accessToken: { ...token, shopAccess },
    });
  }

  private async _confirmDelete() {
    if (!this._confirmShop) return;
    if (
      this._confirmText.trim().toLowerCase() !== this._confirmShop.toLowerCase()
    ) {
      this._message = {
        kind: 'danger',
        text: 'The confirmation text did not match the shop name.',
      };
      return;
    }
    const target = this._confirmShop;
    this._confirmShop = null;
    this._busy = 'deleting';
    this._message = null;
    try {
      const r = await ApiFactory.authenticated().devDeleteShop(target);
      if (!r.success) {
        this._message = {
          kind: 'danger',
          text: r.error ?? `Could not delete ${target}.`,
        };
        return;
      }
      this._message = { kind: 'success', text: `Deleted "${target}".` };
      await this._refreshProfileAndRoute();
    } finally {
      this._busy = 'idle';
      this._confirmText = '';
    }
  }

  // Tier picker: shows the active shop's current subscription snapshot +
  // a dropdown to flip tiers without a payment processor. Only visible
  // when a shop is selected (the API gate is per-shop). Saving fires a
  // refreshed token via the dev endpoint so the user-profile picks up
  // the new tier immediately.
  private _renderTierSection() {
    if (!this.activeShop) return nothing;
    const sub = this.activeShop.subscription;
    const currentTier = (sub?.tier ?? 'free') as SubscriptionTier;
    const status = sub?.status ?? 'trial';
    const trialDays = sub?.trialDaysRemaining;
    return html`
      <section class="realtime-section">
        <h2>
          <sl-icon name="award"></sl-icon>
          Subscription
        </h2>
        <p class="realtime-status">
          <sl-badge variant="primary" pill
            >${TIER_LABELS[currentTier]}</sl-badge
          >
          <sl-badge variant="neutral" pill>${status}</sl-badge>
          ${
            status === 'trial' && trialDays != null
              ? html`<span
                  >${trialDays} day${trialDays === 1 ? '' : 's'} left</span
                >`
              : nothing
          }
          <span style="opacity: 0.7;">
            shop: <code>${this.activeShop.shopName}</code>
          </span>
        </p>
        <div class="actions">
          ${ALL_TIERS.map(
            (t) => html`
              <sl-button
                size="small"
                variant=${t === currentTier ? 'primary' : 'default'}
                outline
                ?loading=${this._busy === 'tier' && t === currentTier}
                ?disabled=${this._busy !== 'idle' || t === currentTier}
                @click=${() => this._setTier(t)}
              >
                ${TIER_LABELS[t]}
              </sl-button>
            `
          )}
        </div>
      </section>
    `;
  }

  private _renderRealtimeSection() {
    const status = this.realtime?.status;
    const stateLabel = status?.disabled
      ? 'disabled'
      : (status?.state ?? 'not started');
    const variant: 'success' | 'warning' | 'neutral' =
      status?.state === 'Connected'
        ? 'success'
        : status?.disabled
          ? 'warning'
          : 'neutral';
    const deviceId = this.realtime?.deviceId;
    const mode = this.realtime?.mode;
    return html`
      <section class="realtime-section">
        <h2>
          <sl-icon name="broadcast"></sl-icon>
          Realtime
        </h2>
        <p class="realtime-status">
          <sl-badge variant=${variant} pill>${stateLabel}</sl-badge>
          ${
            this.activeShop
              ? html`<span
                  >shop: <code>${this.activeShop.shopName}</code></span
                >`
              : html`<span>no shop selected</span>`
          }
        </p>
        <p class="realtime-status">
          <span>
            device:
            <code>${deviceId ?? 'not loaded'}</code>
          </span>
          <span> mode: <code>${mode ?? 'all-in-one'}</code> </span>
        </p>
        <div class="actions">
          <sl-button
            variant="warning"
            outline
            ?loading=${this._busy === 'pinging'}
            ?disabled=${this._busy !== 'idle' || !this.activeShop}
            @click=${this._sendPing}
          >
            <sl-icon slot="prefix" name="send"></sl-icon>
            Send ping
          </sl-button>
        </div>
        <div class="ping-log">
          ${
            this._pingLog.length === 0
              ? html`<span class="ping-log-empty">No pings received yet.</span>`
              : this._pingLog.map(
                  (e) => html`
                    <span class="ping-log-entry">
                      ${e.receivedAt.toLocaleTimeString()} —
                      ${JSON.stringify(e.payload)}
                    </span>
                  `
                )
          }
        </div>
      </section>
    `;
  }

  render() {
    if (!this.isDev) return nothing;

    const ownedShops = (this.userProfile?.shopAccess ?? []).filter(
      (s) => s.active && s.roles.includes('owner')
    );

    return html`
      <section class="dev-card">
        <h2>
          <sl-icon name="tools"></sl-icon>
          ${write`${'ref:dev:title'}Developer tools`}
        </h2>
        <p class="lede">
          ${write`${'ref:dev:lede'}Visible only because your account is in DevUserEmails. Production users never see this.`}
        </p>

        ${this._renderTierSection()}
        ${
          ownedShops.length > 0
            ? html`
                <p class="lede" style="margin-top: 0.85rem;">
                  ${write`${'ref:dev:shop-actions'}Hard-delete or reset a shop you own (no undo for either).`}
                </p>
                <div class="shop-actions-list">
                  ${ownedShops.map(
                    (s) => html`
                      <div class="shop-row">
                        <div class="shop-row-name">
                          ${s.shopDisplayName || s.shopName}
                          <span style="opacity: 0.7;">(${s.shopName})</span>
                          ${
                            s.isSample
                              ? html`<sl-badge
                                  variant="primary"
                                  pill
                                  size="small"
                                  >sample</sl-badge
                                >`
                              : nothing
                          }
                        </div>
                        <div class="shop-row-actions">
                          ${
                            s.isSample
                              ? html`
                                  <sl-button
                                    variant="warning"
                                    outline
                                    size="small"
                                    ?loading=${
                                      this._busy === 'resetting' &&
                                      this._resettingShop === s.shopName
                                    }
                                    ?disabled=${this._busy !== 'idle'}
                                    @click=${() => this._resetSample(s.shopName)}
                                  >
                                    <sl-icon
                                      slot="prefix"
                                      name="arrow-clockwise"
                                    ></sl-icon>
                                    Reset
                                  </sl-button>
                                `
                              : nothing
                          }
                          <sl-button
                            variant="danger"
                            outline
                            size="small"
                            ?disabled=${this._busy !== 'idle'}
                            @click=${() => this._askConfirmDelete(s.shopName)}
                          >
                            <sl-icon slot="prefix" name="trash"></sl-icon>
                            Delete
                          </sl-button>
                        </div>
                      </div>
                    `
                  )}
                </div>
              `
            : nothing
        }
        ${
          this._message
            ? html`
                <sl-alert
                  variant=${this._message.kind === 'success' ? 'success' : 'danger'}
                  open
                  closable
                  @sl-after-hide=${() => (this._message = null)}
                >
                  ${this._message.text}
                </sl-alert>
              `
            : nothing
        }
        ${this._renderRealtimeSection()}

        <sl-dialog
          label="Delete shop"
          ?open=${this._confirmShop !== null}
          @sl-request-close=${(e: Event) => {
            e.preventDefault();
            this._cancelConfirm();
          }}
        >
          <p>
            This will permanently delete <code>${this._confirmShop}</code> and
            <strong>all</strong> its data — sales, stock, menu, stocktakes,
            members. There is no undo.
          </p>
          <p>Type <code>${this._confirmShop}</code> below to confirm.</p>
          <sl-input
            placeholder="shop name"
            .value=${this._confirmText}
            @sl-input=${(e: Event) =>
              (this._confirmText = (e.target as HTMLInputElement).value)}
          ></sl-input>
          <sl-button
            slot="footer"
            variant="default"
            @click=${this._cancelConfirm}
          >
            Cancel
          </sl-button>
          <sl-button
            slot="footer"
            variant="danger"
            ?loading=${this._busy === 'deleting'}
            ?disabled=${
              this._confirmText.trim().toLowerCase() !==
              (this._confirmShop ?? '').toLowerCase()
            }
            @click=${this._confirmDelete}
          >
            Delete forever
          </sl-button>
        </sl-dialog>
      </section>
    `;
  }
}
